import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { NoDataComponent } from '@zaoblako/web/shared/components/no-data/no-data.component';
import { VisualSelectedColumnsTableDirective } from '@zaoblako/web/shared/directives/visual-selected-columns-table.directive';
import { VisualSelectedRowsTableDirective } from '@zaoblako/web/shared/directives/visual-selected-rows-table.directive';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { FilterEmptyPipe } from '@zaoblako/web/shared/pipes/filter-empty.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';

import { DomHandler } from 'primeng/dom';
import { SkeletonModule } from 'primeng/skeleton';
import { Table, TableModule, TableService } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ObjectUtils } from 'primeng/utils';

// НЕ ДОБАВЛЯТЬ changeDetection, убьет!
@Component({
  standalone: true,
  selector: 'crm-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  imports: [
    CommonModule,
    TableModule,
    OrderByPipe,
    MultiSelectComponent,
    SkeletonModule,
    SpinnerComponent,
    NoDataComponent,
    FilterEmptyPipe,
    VisualSelectedRowsTableDirective,
    VisualSelectedColumnsTableDirective,
    TooltipModule
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    DomHandler,
    ObjectUtils,
    TableService,
    {
      provide: Table,
      useFactory: (crmTable: TableComponent) => crmTable.table,
      deps: [TableComponent]
    }
  ]
})
export class TableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('table', { static: true }) private table: Table;

  @ContentChild('headerTemplate') public headerTemplate;
  @ContentChild('groupheaderTemplate') public groupheaderTemplate;
  @ContentChild('rowTemplate') public rowTemplate;
  @ContentChild('footerTemplate') public footerTemplate;
  @ContentChild('captionTemplate') public captionTemplate;

  @Input() public loading = false;
  @Input() public paginationLoading = false;

  @Input() public selectable = false;
  @Input() public selectionDisabled = false;
  @Input() public selectionMode: 'single' | 'multiple' | undefined | null;
  @Input() public dataKey = 'id';
  @Input() public selectedItems: any[] = [];
  @Output() public selectedItemsChange = new EventEmitter<any[]>();

  public get isSingleSelection(): boolean {
    return this.selectionMode === 'single';
  }

  public get resolvedSelectionMode(): 'single' | 'multiple' {
    return this.isSingleSelection ? 'single' : 'multiple';
  }

  public get tableSelection(): any {
    if (this.isSingleSelection) {
      return this.selectedItems?.[0] ?? null;
    }
    return this.selectedItems;
  }

  public set tableSelection(value: any) {
    if (this.isSingleSelection) {
      this.selectedItems = value ? [value] : [];
    } else {
      this.selectedItems = value ?? [];
    }
    this.selectedItemsChange.emit(this.selectedItems);
  }

  @Output() public filterColumnsChange = new EventEmitter<string[]>();

  @Input() public name: string;
  @Input() public values: any[] = [{}];
  @Input() public columns: CrmTableColumn[] = [];
  public selectedColumns: CrmTableColumn[] = [];
  public selectedColumnsMap: Record<string, boolean>;

  @Input() public filterColumns = false;

  @Input() public sort = '';
  @Output() public sortChange = new EventEmitter<string>();

  @Input() public inplaceSort = true;
  @Input() public styleClass = '';
  @Input() public scrollHeight: string;
  @Input() public scrollable = false;
  @Input() public responsiveLayout: string;

  @Input() public groupRowsBy: string;
  @Input() public rowGroupMode: 'subheader' | 'rowspan' | undefined;

  @Input() public autoLayout = false;
  @Input() public isVisualSelected = true;
  @Input() public isVisualSelectedColumns = false;

  @Input() public skeletonItemsCount = 15;
  public skeletonItems = [];

  @Input() public selectedColumnNames: string[] | undefined;

  /**
   * Оболочка реестра v2 (.rpt-standard-table-wrap + .rpt-registry-v2-skin).
   * null — авто: при `card-table` в styleClass и без scrollable/scrollHeight; false — выкл.; true — вкл. (кроме scrollable/scrollHeight).
   */
  @Input() public registryTableChrome: boolean | null = null;

  public useRegistryTableChrome = false;

  public constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly el: ElementRef,
    private readonly renderer: Renderer2
  ) {}

  public ngOnInit(): void {
    if (!this.sort) {
      this.sort =
        localStorage.getItem(`table.${this.name}.sort`) ??
        this.columns.find((column) => column.sort && column.defaultSort)?.sort ??
        this.columns.find((column) => column.sort)?.sort ??
        '';
    }

    this.skeletonItems = Array(this.skeletonItemsCount).fill({});

    this.setSelectedColumns();
    this.updateRegistryTableChrome();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.columns?.currentValue) {
      this.columns.forEach((element, index) => {
        if (!element.order) {
          element.order = index;
        }
      });
    }

    if (changes.columns?.currentValue || changes.selectedColumnNames?.currentValue) {
      this.setSelectedColumns();
    }

    if (!changes.groupRowsBy?.currentValue && changes.groupRowsBy?.previousValue) {
      this.loading = true;

      setTimeout(() => {
        this.loading = false;
        this.cdRef.markForCheck();
      }, 100);
    }

    if (
      changes.styleClass ||
      changes.scrollable ||
      changes.scrollHeight ||
      changes.registryTableChrome
    ) {
      this.updateRegistryTableChrome();
    }
  }

  private updateRegistryTableChrome(): void {
    const scrollOff = !this.scrollable && !this.scrollHeight;
    if (this.registryTableChrome === false) {
      this.useRegistryTableChrome = false;
      return;
    }
    if (this.registryTableChrome === true) {
      this.useRegistryTableChrome = scrollOff;
      return;
    }
    const tokens = (this.styleClass ?? '').split(/\s+/).filter(Boolean);
    this.useRegistryTableChrome = tokens.includes('card-table') && scrollOff;
  }

  private initialHeaderDocumentOffsetTop = 0;
  private scrollListener!: () => void;
  private resizeListener!: () => void;

  private mutationObserver!: MutationObserver;

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.shouldDisableWindowStickyHeader()) {
        this.clearWindowStickyHeaderTransform();
        return;
      }

      this.calculateHeaderOffset();
      this.scrollListener = this.renderer.listen('window', 'scroll', () => {
        this.onScroll();
      });
      this.resizeListener = this.renderer.listen('window', 'resize', () => {
        this.onResize();
      });
      this.setupMutationObserver();
      this.onScroll();
    }, 0);
  }

  public ngOnDestroy(): void {
    if (this.scrollListener) this.scrollListener();
    if (this.resizeListener) this.resizeListener();
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  private setupMutationObserver(): void {
    const targetNode = document.body.querySelector('.main-content') || document.body;
    if (!targetNode) {
      return;
    }

    this.mutationObserver = new MutationObserver((mutationsList: MutationRecord[]) => {
      let externalChangeDetected = false;
      for (const mutation of mutationsList) {
        if (!this.el.nativeElement.contains(mutation.target)) {
          externalChangeDetected = true;
          break;
        }
      }
      if (externalChangeDetected) {
        this.calculateHeaderOffset();
        this.onScroll();
      }
    });
    this.mutationObserver.observe(targetNode, { childList: true, subtree: true });
  }

  private calculateHeaderOffset(): void {
    const tableHeader = this.el.nativeElement.querySelector('.p-datatable-thead');
    if (tableHeader) {
      const rect = tableHeader.getBoundingClientRect();
      this.initialHeaderDocumentOffsetTop = rect.top + window.scrollY;
    }
  }

  public onResize(): void {
    this.calculateHeaderOffset();
    this.onScroll();
  }

  public onScroll(): void {
    const tableHeader = this.el.nativeElement.querySelector('.p-datatable-thead');
    if (!tableHeader) return;

    const currentWindowScrollTop = window.scrollY;

    if (currentWindowScrollTop >= this.initialHeaderDocumentOffsetTop) {
      const translateYValue = currentWindowScrollTop - this.initialHeaderDocumentOffsetTop;
      this.renderer.setStyle(tableHeader, 'transform', `translateY(${translateYValue}px)`);
    } else {
      this.renderer.removeStyle(tableHeader, 'transform');
    }
  }

  public setSelectedColumns() {
    if (this.name?.length) {
      const selectedColumnsNames = localStorage.getItem(`table.${this.name}.columns`);
      this.selectedColumns = selectedColumnsNames
        ? this.columns.filter((col) => selectedColumnsNames.includes(col.name))
        : [...this.columns];
    }
    this.selectedColumns = this.selectedColumnNames
      ? this.columns.filter((col) => this.selectedColumnNames.includes(col.name) || !col.name || col.required)
      : [...this.columns];
    this.updateSelectedColumnsMap();
  }

  public getSortIconClass(sort: string): string {
    const type = sort.split(':')?.[1];
    if (type === 'number') {
      return this.sort?.includes(sort)
        ? this.sort[0] === '-'
          ? 'pi pi-sort-numeric-up'
          : 'pi pi-sort-numeric-down'
        : 'pi pi-sort-alt';
    } else if (['date', 'boolean'].includes(type)) {
      return this.sort?.includes(sort) ? (this.sort[0] === '-' ? 'pi pi-sort-up' : 'pi pi-sort-down') : 'pi pi-sort';
    } else {
      return this.sort?.includes(sort)
        ? this.sort[0] === '-'
          ? 'pi pi-sort-alpha-up'
          : 'pi pi-sort-alpha-down'
        : 'pi pi-sort-alt';
    }
  }

  public setSort(sort: string): void {
    this.sort = this.sort === sort ? (sort[0] === '-' ? sort.substring(1) : `-${sort}`) : sort;
    if (this.name?.length) {
      localStorage.setItem(`table.${this.name}.sort`, this.sort);
    }
    if (this.groupRowsBy) {
      this.values = [...this.values];
    }

    this.sortChange.emit(this.sort);
  }

  /** Для кастомного `#headerTemplate` с `crm-table-sort-header`: готовое значение `sort` из дочернего компонента. */
  public onCustomHeaderSort = (nextSort: string): void => {
    this.sort = nextSort;
    if (this.name?.length) {
      localStorage.setItem(`table.${this.name}.sort`, this.sort);
    }
    if (this.groupRowsBy) {
      this.values = [...this.values];
    }
    this.sortChange.emit(this.sort);
  };

  public updateSelectedColumnsMap() {
    this.selectedColumnsMap = {};
    this.selectedColumns.forEach((col) => (this.selectedColumnsMap[col.name] = true));
  }

  public setFilterColumns(selectedFromPanel: CrmTableColumn[]): void {
    let next: CrmTableColumn[] =
      selectedFromPanel?.length ? [...selectedFromPanel] : [...this.columns];

    const requiredExtra: CrmTableColumn[] = this.columns.filter(
      (column) => column.required && column.name && !next.some((item) => item.name === column.name)
    );
    if (requiredExtra.length) {
      next = [...next, ...requiredExtra];
    }

    next.sort((columnA, columnB) => (columnA.order ?? 0) - (columnB.order ?? 0));

    this.selectedColumns = next;
    this.updateSelectedColumnsMap();
    if (this.name?.length) {
      localStorage.setItem(`table.${this.name}.columns`, JSON.stringify(this.selectedColumns.map((col) => col.name)));
    }
    this.filterColumnsChange.emit(this.selectedColumns.map((col) => col.name));
  }

  public toColumn(column: any): CrmTableColumn {
    return column as CrmTableColumn;
  }

  public getTableClass(): string {
    if (this.scrollable || this.scrollHeight) {
      return this.styleClass;
    }
    if (this.shouldDisableWindowStickyHeader()) {
      return this.styleClass;
    }
    return `sticky-header ${this.styleClass}`.trim();
  }

  /** Window-scroll sticky (transform на thead) — не для таблиц внутри виджетов карточки v2. */
  private shouldDisableWindowStickyHeader(): boolean {
    const host = this.el?.nativeElement as HTMLElement | undefined;
    return !!host?.closest('.frm-card-v2-embed');
  }

  private clearWindowStickyHeaderTransform(): void {
    const tableHeader = this.el.nativeElement.querySelector('.p-datatable-thead') as HTMLElement | null;
    if (tableHeader) {
      this.renderer.removeStyle(tableHeader, 'transform');
    }
  }

  public get emptyMessageColSpan(): number {
    const columnCount = this.selectedColumns?.length ?? 0;
    return Math.max(1, (this.selectable ? 1 : 0) + columnCount);
  }
}
